import { flatten } from 'lodash'
import dayjs, { Dayjs } from 'dayjs'
import { ContentType, Schedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import {
  BrandPlanContents,
  BrandPlanSchedule,
  BrandPlanScheduleContentStatus,
  PostContentType,
  SuggestionRequestStatus,
} from 'types'
import { MinimalMediaFile, platformManualSchedulingMapping } from '../../Types'
import { Additionals } from './Types'
import { sum } from 'lodash'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { CONTENT_TYPE_TO_TACTIC, getTacticInfo } from 'components/TacticIntegrationList/utils'

export const channelTypeToField: Record<ContentType, string> = {
  instagram_post: 'instagram_contents',
  instagram_story: 'instagram_contents',
  instagram_reel: 'instagram_contents',
  tiktok_post: 'tiktok_contents',
  youtube_short: 'youtube_contents',
  facebook_post: 'facebook_contents',
  facebook_story: 'facebook_contents',
  linkedin_post: 'linkedin_contents',
  twitter_post: 'twitter_contents',
  twitter_thread: 'twitter_contents',
  blog_post: 'blog_contents',
  newsletter: 'newsletter_contents',
  crosspost_post: 'crosspost_contents',
  video_crosspost_post: 'video_crosspost_contents',
  google_my_business_post: 'google_my_business_contents',
}

const getExcludedAccountIdsForContentType = (
  contentType: ContentType,
  excludedAccounts: Record<number, boolean>,
  externalIntegrations: ExternalIntegration[],
): number[] => {
  const tactic = CONTENT_TYPE_TO_TACTIC[contentType]
  if (!tactic) {
    return []
  }

  const tacticInfo = getTacticInfo(tactic)
  if (!tacticInfo) {
    return []
  }

  const relevantProviders = [tacticInfo.integrationType]

  const relevantAccounts = externalIntegrations.filter((integration) =>
    relevantProviders.includes(integration.provider),
  )

  return relevantAccounts.filter((account) => excludedAccounts[account.id] === true).map((account) => account.id)
}

/**
 * Day offset from the effective campaign start for the post at `index` (0-based).
 * Spreads `postCount` posts across the inclusive range `[0, daysDiff]` so longer
 * campaigns get proportional per-week distribution. When there are
 * more posts than days, falls back to cycling through available days.
 */
export const getDayOffsetForCampaignSpread = (index: number, postCount: number, daysDiff: number): number => {
  if (daysDiff <= 0 || postCount <= 1) {
    return 0
  }
  if (postCount <= daysDiff + 1) {
    return Math.round((index * daysDiff) / (postCount - 1))
  }
  return index % (daysDiff + 1)
}

export type CrosspostPostCountsByContentType = Record<PostContentType, number>

const buildCrosspostContentTypeOrder = (
  num: number,
  byType?: CrosspostPostCountsByContentType,
): PostContentType[] => {
  if (num <= 0) return []
  if (!byType) {
    return Array.from({ length: num }, () => 'still_image' as PostContentType)
  }
  const specs: PostContentType[] = [
    ...Array(byType.still_image).fill('still_image' as const),
    ...Array(byType.carousel).fill('carousel' as const),
    ...Array(byType.video).fill('video' as const),
  ]
  if (specs.length < num) {
    return specs.concat(Array(num - specs.length).fill('still_image' as const))
  }
  if (specs.length > num) {
    return specs.slice(0, num)
  }
  return specs
}

export const generateSchedule = (
  additionals: Additionals,
  topic: string,
  mediaFiles: MinimalMediaFile[],
  targetSchedule: BrandPlanSchedule,
  excludedAccounts: Record<number, boolean>,
  externalIntegrations: ExternalIntegration[],
  date?: Dayjs,
  storyCrosspost?: boolean,
  crosspostPostCountsByContentType?: CrosspostPostCountsByContentType,
) => {
  const newSchedule = (Object.keys(additionals) as ContentType[]).map((key: ContentType) => {
    const num = additionals[key]
    const crosspostContentTypes =
      key === 'crosspost_post' ? buildCrosspostContentTypeOrder(num, crosspostPostCountsByContentType) : null

    const randomDay = (index: number) => {
      const startDate = dayjs(targetSchedule.start_date)
      const endDate = startDate.add(targetSchedule.length * 7, 'days')
      const today = dayjs()
      const tomorrow = today.add(1, 'day')
      const minDate = startDate.isAfter(tomorrow) ? startDate : tomorrow
      const effectiveStartDate = minDate.isAfter(endDate) ? endDate : minDate
      const daysDiff = endDate.diff(effectiveStartDate, 'day')
      const dayOffset = getDayOffsetForCampaignSpread(index, num, daysDiff)

      const postAt = effectiveStartDate.add(dayOffset, 'day')
      return postAt
    }

    const excludedAccountIds = getExcludedAccountIdsForContentType(key, excludedAccounts, externalIntegrations)
    const isCrosspost = storyCrosspost && key === 'instagram_story'

    return Array.from(
      { length: num },
      (_, index) =>
        ({
          type: key,
          topic: topic,
          post_at: date || randomDay(index),
          initial_media_file_ids: undefined,
          ...(excludedAccountIds.length > 0 && { excluded_account_ids: excludedAccountIds }),
          ...(isCrosspost && { is_crosspost: true }),
          ...(crosspostContentTypes && { content_type: crosspostContentTypes[index] }),
        } as Schedule),
    )
  })

  const flattenedSchedule = flatten(newSchedule)
  flattenedSchedule.forEach((content, index) => {
    const mediaFile = mediaFiles[index]
    if (mediaFile) {
      content.initial_media_file_ids = [mediaFile.id]
    }
  })

  // Distribute times within platform-specific windows by type and weekday/weekend
  const postsByDateAndType = new Map<string, Schedule[]>()
  flattenedSchedule.forEach((content) => {
    const dateKey = content.post_at.format('YYYY-MM-DD')
    const key = `${dateKey}__${content.type}`
    const list = postsByDateAndType.get(key) || []
    list.push(content)
    postsByDateAndType.set(key, list)
  })

  postsByDateAndType.forEach((posts) => {
    const sample = posts[0]
    const isWeekend = [0, 6].includes(sample.post_at.day())
    const bestTimes = platformManualSchedulingMapping[sample.type]?.best_times_of_day_to_post
    const bestTimeToPost = isWeekend ? bestTimes?.weekends : bestTimes?.weekdays
    const window = bestTimeToPost ? bestTimeToPost : { from: 11, to: 15 }

    const fromHour = window.from
    const toHour = window.to
    const totalMinutes = Math.max((toHour - fromHour) * 60, 0)
    const count = posts.length

    if (count === 1) {
      const minutesFromStart = Math.round(totalMinutes / 2)
      const hour = fromHour + Math.floor(minutesFromStart / 60)
      const minute = minutesFromStart % 60
      posts[0].post_at = posts[0].post_at.hour(hour).minute(minute).second(0).millisecond(0)

      return
    }

    const stepMinutes = count > 1 ? totalMinutes / (count - 1) : 0
    posts.forEach((post, i) => {
      const minutesFromStart = Math.round(stepMinutes * i)
      const hour = fromHour + Math.floor(minutesFromStart / 60)
      const minute = minutesFromStart % 60
      post.post_at = post.post_at.hour(hour).minute(minute).second(0).millisecond(0)
    })
  })

  return flattenedSchedule
}

const STORY_TYPES = ['instagram_story', 'facebook_story']

export const calculateChannelCounts = (targetSchedule?: BrandPlanSchedule) => {
  return targetSchedule?.contents.reduce((acc, content) => {
    if (
      ([SuggestionRequestStatus.Failed, SuggestionRequestStatus.Expired] as BrandPlanScheduleContentStatus[]).includes(
        content.status,
      )
    ) {
      return acc
    }
    if (content.is_crosspost) {
      // Story crossposts count as instagram_story (the primary type), not crosspost_post
      if (STORY_TYPES.includes(content.type)) {
        acc['instagram_story'] = (acc['instagram_story'] || 0) + 1
      } else {
        acc['crosspost_post'] = (acc['crosspost_post'] || 0) + 1
      }
      return acc
    }
    const contentType = content.type as ContentType
    if (!contentType) return acc
    acc[contentType] = (acc[contentType] || 0) + 1
    return acc
  }, {} as Record<ContentType, number>)
}

export const calculateTotalPostCount = (targetSchedule?: BrandPlanSchedule) => {
  const channelCounts = calculateChannelCounts(targetSchedule)
  return sum(Object.values(channelCounts || {}))
}

export const getCountFromDirectProperties = (channelType: ContentType, targetSchedule?: BrandPlanContents): number => {
  const fieldName = channelTypeToField[channelType]
  if (!fieldName || !targetSchedule) return 0

  // Map field names to actual schedule properties
  const fieldToProperty: Record<string, keyof BrandPlanContents> = {
    instagram_contents: 'instagram_contents',
    facebook_contents: 'facebook_contents',
    linkedin_contents: 'linkedin_contents',
    twitter_contents: 'twitter_contents',
    tiktok_contents: 'tiktok_contents',
    youtube_contents: 'youtube_contents',
    blog_contents: 'seo_contents',
    newsletter_contents: 'email_contents',
    crosspost_contents: 'crosspost_contents',
    video_crosspost_contents: 'video_crosspost_contents',
    google_my_business_contents: 'google_my_business_contents',
  }

  const propertyName = fieldToProperty[fieldName]
  if (!propertyName) return 0

  const contents = targetSchedule[propertyName] as Array<{ type: string; count: number }> | undefined
  if (!contents || !Array.isArray(contents)) return 0

  // For Instagram, match the specific type (post, story, reel)
  if (fieldName === 'instagram_contents') {
    const typeMap: Partial<Record<ContentType, string>> = {
      instagram_post: 'post',
      instagram_story: 'story',
      instagram_reel: 'reel',
    }
    const targetType = typeMap[channelType]
    if (!targetType) return 0
    const matchingEntry = contents.find((entry) => entry.type === targetType)
    return matchingEntry?.count || 0
  }

  // For Facebook, match the specific type (post, story)
  if (fieldName === 'facebook_contents') {
    const typeMap: Partial<Record<ContentType, string>> = {
      facebook_post: 'post',
      facebook_story: 'story',
    }
    const targetType = typeMap[channelType]
    if (!targetType) return 0
    const matchingEntry = contents.find((entry) => entry.type === targetType)
    return matchingEntry?.count || 0
  }

  // For other channels, sum all counts (usually just one entry)
  return contents.reduce((sum, entry) => sum + (entry.count || 0), 0)
}

export const getScheduleContentTypeCounts = (
  schedule?: BrandPlanContents & { length: number },
): Record<ContentType, number> => {
  const instagramPosts = getCountFromDirectProperties('instagram_post', schedule)
  const instagramReels = getCountFromDirectProperties('instagram_reel', schedule)
  const instagramStories = getCountFromDirectProperties('instagram_story', schedule)
  const facebookPosts = getCountFromDirectProperties('facebook_post', schedule)
  const facebookStories = getCountFromDirectProperties('facebook_story', schedule)
  const twitterPosts = getCountFromDirectProperties('twitter_post', schedule)
  const twitterThreads = getCountFromDirectProperties('twitter_thread', schedule)
  const linkedinPosts = getCountFromDirectProperties('linkedin_post', schedule)
  const tiktokPosts = getCountFromDirectProperties('tiktok_post', schedule)
  const youtubeShorts = getCountFromDirectProperties('youtube_short', schedule)
  const blogPosts = getCountFromDirectProperties('blog_post', schedule)
  const newsletters = getCountFromDirectProperties('newsletter', schedule)
  const crosspostPosts = getCountFromDirectProperties('crosspost_post', schedule)
  const videoCrosspostPosts = getCountFromDirectProperties('video_crosspost_post', schedule)
  const googleMyBusinessPosts = getCountFromDirectProperties('google_my_business_post', schedule)

  return {
    instagram_post: instagramPosts * (schedule?.length || 1),
    instagram_reel: instagramReels * (schedule?.length || 1),
    instagram_story: instagramStories * (schedule?.length || 1),
    facebook_post: facebookPosts * (schedule?.length || 1),
    facebook_story: facebookStories * (schedule?.length || 1),
    twitter_post: twitterPosts * (schedule?.length || 1),
    twitter_thread: twitterThreads * (schedule?.length || 1),
    linkedin_post: linkedinPosts * (schedule?.length || 1),
    tiktok_post: tiktokPosts * (schedule?.length || 1),
    youtube_short: youtubeShorts * (schedule?.length || 1),
    blog_post: blogPosts * (schedule?.length || 1),
    newsletter: newsletters * (schedule?.length || 1),
    crosspost_post: crosspostPosts * (schedule?.length || 1),
    video_crosspost_post: videoCrosspostPosts * (schedule?.length || 1),
    google_my_business_post: googleMyBusinessPosts * (schedule?.length || 1),
  }
}
